/**
 * Fase 2 — portal PÚBLICO de autofacturación (/factura/:code), sin login.
 * El cliente del tenant abre el QR/link del ticket, captura sus datos
 * fiscales y el sistema timbra vía el MISMO kernel del staff. Estados:
 *   - available: formulario de captura.
 *   - already_stamped: muestra el CFDI existente + descargas (idempotencia
 *     VISIBLE: re-facturar jamás es error).
 *   - out_of_window / issuer_not_ready / module_off / not_closed: mensaje claro.
 *   - fallo de timbrado: error EN PANTALLA para re-captura + aviso al correo
 *     entregado con el link de corrección (lo manda el backend).
 * Patrón UnsubscribePage/booking público: pathname + fetch crudo (sin Bearer).
 */
import { useEffect, useState } from 'react';
import { API_BASE } from '../lib/api';
import {
  FiscalData, FiscalDataFields, EMPTY_FISCAL, fiscalErrMsg,
} from '../components/FiscalDataFields';
import { avisoLegend } from './avisoLegend';

const PATH_RE = /^\/factura\/([0-9a-fA-F]{16})\/?$/;

export const isSelfInvoiceRoute = (pathname: string): boolean => PATH_RE.test(pathname);

interface TicketInfo {
  status: 'available' | 'already_stamped' | 'out_of_window' | 'module_off' | 'issuer_not_ready' | 'not_closed';
  tenantName: string;
  issuerRazon: string | null;
  issuerRfc: string | null;
  total: number;
  currency: string;
  closedAt: string | null;
  windowUntil: string | null;
  folio: string | null;
  canDownload: boolean;
  /** #2 — URL del aviso de privacidad del tenant (null = sin publicar). */
  avisoUrl?: string | null;
  error?: string | null;
}

const BLOCKED_COPY: Record<string, string> = {
  not_closed: 'Este ticket aún no está cerrado/pagado. Pide en el mostrador que cierren la venta e inténtalo de nuevo.',
  issuer_not_ready: 'Este negocio aún no tiene activada la facturación en línea. Solicita tu factura directamente en el mostrador.',
  module_off: 'Este negocio aún no tiene activada la facturación en línea para este tipo de venta.',
  out_of_window: 'El plazo para autofacturar este ticket ya venció. Contacta directamente al negocio.',
};

async function publicJson<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body, message: body?.message };
  return body as T;
}

export function SelfInvoicePage() {
  const code = window.location.pathname.match(PATH_RE)?.[1]?.toLowerCase() ?? '';
  const [info, setInfo] = useState<TicketInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState<FiscalData>(EMPTY_FISCAL);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [errCode, setErrCode] = useState<string | null>(null); // #573: distingue pago-incompleto de datos-invalidos

  useEffect(() => {
    publicJson<TicketInfo>(`/public/factura/${code}`)
      .then(setInfo)
      .catch(() => setNotFound(true));
  }, [code]);

  const submit = async () => {
    setBusy(true); setErr(null); setErrCode(null);
    try {
      const r = await publicJson<TicketInfo>(`/public/factura/${code}`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          rfc: form.rfc.trim().toUpperCase(),
          cp: form.cp.trim(),
          email: email.trim(),
        }),
      });
      setInfo(r);
      // Fallo de timbrado: el error viene en la respuesta → re-captura aquí
      // mismo (y el backend ya avisó al correo con el link de corrección).
      if (r.error) setErr(r.error);
    } catch (e: any) {
      setErrCode(e?.body?.code ?? null);  // #573: p.ej. PAYMENT_INCOMPLETE (no es error de datos del cliente)
      setErr(fiscalErrMsg(e, 'No se pudo emitir la factura. Revisa tus datos e inténtalo de nuevo.'));
    } finally { setBusy(false); }
  };

  const download = (kind: 'xml' | 'pdf') => {
    window.location.href = `${API_BASE}/public/factura/${code}/download/${kind}`;
  };

  const shellCls = 'mx-auto w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm';
  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className={shellCls}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Autofacturación</p>
        <h1 className="mt-1 font-serif text-xl text-slate-900">
          {info?.issuerRazon ?? info?.tenantName ?? 'Factura tu compra'}
        </h1>
        {info?.issuerRfc && <p className="text-xs text-slate-500">RFC emisor: <span className="font-mono">{info.issuerRfc}</span></p>}

        {notFound && (
          <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Ticket no encontrado. Verifica el código impreso en tu ticket o escanea de nuevo el QR.
          </p>
        )}

        {info && (
          <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="flex justify-between">
              <span>Compra{info.closedAt ? ` · ${new Date(info.closedAt).toLocaleDateString('es-MX')}` : ''}</span>
              <strong>${Number(info.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })} {info.currency}</strong>
            </div>
            {info.status === 'available' && info.windowUntil && (
              <p className="mt-1 text-xs text-slate-500">
                Puedes facturar hasta el {new Date(info.windowUntil).toLocaleDateString('es-MX')}.
              </p>
            )}
          </div>
        )}

        {info && info.status === 'already_stamped' && (
          <div className="mt-4 space-y-3">
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Este ticket ya tiene factura ✓ — folio fiscal:{' '}
              <strong className="break-all">{info.folio}</strong>
            </p>
            <div className="flex gap-2">
              <button onClick={() => download('xml')}
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                Descargar XML
              </button>
              <button onClick={() => download('pdf')}
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                Descargar PDF
              </button>
            </div>
          </div>
        )}

        {info && BLOCKED_COPY[info.status] && (
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {BLOCKED_COPY[info.status]}
          </p>
        )}

        {info && info.status === 'available' && (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-slate-600">
              Captura tus datos fiscales <strong>tal como aparecen en tu Constancia de Situación Fiscal</strong>.
            </p>
            <FiscalDataFields form={form} onChange={setForm} compact />
            <div>
              <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                Correo (ahí te enviamos XML y PDF)
              </label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                placeholder="tu@correo.com" className={inputCls} />
            </div>
            {err && (
              <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {errCode === 'PAYMENT_INCOMPLETE'
                  ? <>{err}</>  /* #573: pago incompleto -> NO decir "corrige tus datos" (no es del cliente) */
                  : <>{err} — corrige tus datos y vuelve a intentar. También te enviamos este aviso por correo.</>}
              </p>
            )}
            {/* #2 (smoke 08/09) — leyenda de privacidad OBLIGATORIA: este formulario
                recibe datos fiscales; misma exigencia que tienda y reservas. */}
            {(() => { const lg = avisoLegend(info.tenantName, info.avisoUrl ?? null); return (
              <p className="rounded-xl bg-slate-50 px-4 py-3 text-[11px] leading-relaxed text-slate-500">
                {lg.text}{' '}
                {lg.avisoUrl && (
                  <a href={lg.avisoUrl} target="_blank" rel="noreferrer" className="font-medium underline">
                    {lg.linkLabel}
                  </a>
                )}
              </p>
            ); })()}
            <button onClick={submit} disabled={busy || !email.trim()}
              className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
              {busy ? 'Timbrando…' : 'Emitir mi factura'}
            </button>
            <p className="text-center text-[11px] text-slate-400">
              Facturación operada a través de FluidSol. Tus datos solo se usan para emitir tu CFDI.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
